import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { sanitizeQty, toLatinDigits } from "@/lib/wmDigits";

/**
 * Quantity field with the unit as a suffix. Accepts Arabic-Indic digits and
 * the Arabic decimal separator; a piece-counted unit (حبة / كوب / علبة)
 * refuses a decimal point outright instead of failing on save.
 *
 * RTL-safe: the WRAPPER is `dir="ltr"`, so the suffix's `end-0` and the
 * input's `pe-14` are the same physical side and the Arabic unit label never
 * sits over the digits (the stock count's pattern, frontend-patterns §9).
 */
export function QuantityInput({
    value, onChange, unitLabel, allowsDecimal, testId, warnAbove, autoFocus, invalid, onEnter,
}) {
    const { t } = useI18n();
    const num = Number(toLatinDigits(value || ""));
    const warn = warnAbove && !Number.isNaN(num) && num >= warnAbove;
    return (
        <div className="min-w-0">
            <div className="relative" dir="ltr">
                <Input
                    data-testid={testId}
                    value={value ?? ""}
                    onChange={(e) => onChange(sanitizeQty(e.target.value, allowsDecimal))}
                    onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
                    inputMode={allowsDecimal ? "decimal" : "numeric"}
                    autoFocus={autoFocus}
                    placeholder={t("wm_qty_placeholder")}
                    dir="ltr"
                    className={`h-10 pe-14 text-base font-semibold tabular-nums ${
                        invalid ? "border-[color:var(--danger)]" : ""
                    }`}
                />
                {unitLabel ? (
                    <span className="pointer-events-none absolute end-0 top-0 flex h-10 items-center pe-3 text-xs font-medium text-[color:var(--ink-muted)]">
                        {unitLabel}
                    </span>
                ) : null}
            </div>
            {warn ? (
                <p className="mt-1 text-[11px] text-[color:var(--brand-orange)]">{t("wm_warn_big_qty")}</p>
            ) : null}
        </div>
    );
}
